import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faGamepad } from '@fortawesome/free-solid-svg-icons';

import GameIcon from '../../components/game-icon/GameIcon.jsx';
import { findGameBySlug } from '../../utils/gameSlug.js';
import { gamePageLinks, onboardingStepLinks } from '../../routes/gameScopeNav.jsx';
import { filterLinksByRole } from '../../utils/rbacUtils.js';
import useNavFlywheels from '../../pages/game/live-services/flywheels/hooks/useNavFlywheels.js';
import { portfolioListItems, portfolioPageLinks } from '../../routes/portfolioScopeNav.jsx';
import { gameEntryPath, isOnboardingPath, onboardingGameId } from '../../routes/onboardingSteps.js';
import { DEFAULT_SCOPE } from '../../routes/navScopes.jsx';

/**
 * The nav tree a view shows: the scope's links (`useNavScope`) with the game, onboarding-step or portfolio pages
 * swapped in for the URL. The shell builds it once and shares it through NavTreeContext - the sidebar renders it,
 * and the page header's section switcher walks it - so both always list the same pages.
 *
 * Built once because it loads the open game's flywheels (the Flywheels row's children).
 *
 * @param {Array} navLinks - the scope's RBAC-filtered links
 * @param {{ id: string }} scope - the focused scope from useNavScope
 * @returns {Array} the view's nav tree, without the Pinned section
 */
export const useScopedNavLinks = (navLinks, scope) => {
    const { pathname: activePath } = useLocation();
    const userGames = useSelector(state => state.userGames);
    const userPortfolios = useSelector(state => state.userPortfolios);
    // The game's pages carry their RBAC page (gameScopeNav `page` -> pagePath): filter them like every other
    // nav scope, so a page the role (or a user override) denies is not offered - its route would refuse it.
    const userRole = useSelector(state => state.dashboardContext?.userRole);
    const effectivePermissions = useSelector(state => state.effectivePermissions);
    // Furthest onboarding step reached, published by GameOnboarding — caps the stepper.
    const onboardingMaxStep = useSelector(state => state.dashboardContext?.onboardingDraft?.maxStep);

    // A DRAFT game has no dashboard worth opening yet — it goes to its onboarding wizard,
    // resuming at the step it was left on.
    const gameItems = useMemo(() => (Array.isArray(userGames) ? userGames : [])
        .filter(g => g && g.id)
        .map(g => ({
            path: gameEntryPath(g),
            label: g.gameName || g.id,
            icon: <GameIcon iconUrl={g.gameIconUrl} name={g.gameName || g.id} state={g.state} />,
        })), [userGames]);

    // Inside a game the section becomes that game's pages; outside it, the list of games to open.
    const activeGameSlug = activePath.match(/^\/game\/([^/]+)/)?.[1] || null;
    const activePortfolioSlug = activePath.match(/^\/portfolio\/([^/]+)/)?.[1] || null;
    const isScoped = !!scope && scope.id !== DEFAULT_SCOPE;
    const activeGameId = useMemo(() => findGameBySlug(userGames, activeGameSlug)?.id || null, [userGames, activeGameSlug]);
    const navFlywheels = useNavFlywheels(activeGameId);

    return useMemo(() => {
        // In the hub, bottom-pinned sections are dropped — they live in the HelpPopupButton instead.
        // Inside a focused scope they must render: the admin scope IS a bottom section (you enter it
        // from the popup), so filtering it here would leave the panel empty.
        const body = (navLinks || []).filter(l => isScoped || !l.bottom);
        return body.flatMap(l => {
            // Inside a game/portfolio the section is replaced by that entity's pages, each a
            // top-level section; outside it, the section lists what you can open.
            if (l.scope === 'game') {
                if (isOnboardingPath(activePath)) return onboardingStepLinks(onboardingGameId(activePath), onboardingMaxStep);
                if (activeGameSlug) return effectivePermissions
                    ? filterLinksByRole(gamePageLinks(activeGameSlug, { flywheels: navFlywheels }), userRole, effectivePermissions)
                    : [];
                return [{
                    ...l,
                    subItems: gameItems.length
                        ? gameItems
                        : [{ label: 'No games yet', icon: <FontAwesomeIcon icon={faGamepad} />, isPlaceholder: true }],
                }];
            }
            if (l.scope === 'portfolio') {
                if (activePortfolioSlug) return portfolioPageLinks(activePortfolioSlug);
                const portfolioItems = portfolioListItems(userPortfolios);
                return [{
                    ...l,
                    subItems: portfolioItems.length
                        ? portfolioItems
                        : [{ label: 'No portfolios yet', icon: <FontAwesomeIcon icon={faBriefcase} />, isPlaceholder: true }],
                }];
            }
            return [l];
        });
    }, [navLinks, isScoped, gameItems, userPortfolios, activeGameSlug, activePortfolioSlug, activePath, onboardingMaxStep, userRole, effectivePermissions, navFlywheels]);
};

export default useScopedNavLinks;
